import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useActionsPortefeuille, useBudgetLignes, usePretAccorde } from '@/api/hooks';
import type { DetailPretAccorde } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, Kpi } from '@/components/common';
import { montantDevise, StatutPretAccordeBadge } from '@/components/rh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { dateFr, dateHeureFr } from '@/lib/utils';

const auj = () => new Date().toISOString().slice(0, 10);
type Dialogue = null | 'fed' | 'decaissement' | 'remboursement' | 'reamenagement' | 'exigibilite' | 'imputation' | { contrepasser: string } | { extinction: string };

/** Onglet « Remboursement » du dossier (lot RH-4) : décaissement, échéancier figé, remboursements, retenues, réaménagement, départ de l'agent et recouvrement. */
export function PretAccorde({ demandeId, parPretId = false }: { demandeId: string; parPretId?: boolean }) {
  const { data, isLoading, error } = usePretAccorde(demandeId, parPretId);
  const actions = useActionsPortefeuille();
  const u = useSession()!.utilisateur;
  const [dialogue, setDialogue] = useState<Dialogue>(null);
  if (isLoading) return <Chargement />;
  if (error) return <ErreurBloc erreur={error} />;
  if (!data) return <p className="text-sm text-muted-foreground">Aucun prêt accordé : le prêt est créé à l'approbation finale de la demande.</p>;
  const d = data;
  const p = d.pret;
  const dev = p.devise;
  const a = d.actions;
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setDialogue(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };
  const mien = p.agent.id === u.agentId;
  return (
    <div className="space-y-4">
      <Card data-testid="pret-accorde">
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex flex-wrap items-center gap-2">
              Prêt {p.numero} <StatutPretAccordeBadge statut={p.statut} />
              {p.dateExigibilite && <Badge variant="destructive">exigible depuis le {dateFr(p.dateExigibilite)}</Badge>}
            </span>
            <span className="flex flex-wrap gap-2">
              {a.etablirFed && <Button size="sm" onClick={() => setDialogue('fed')}>Établir le FED</Button>}
              {a.constaterDecaissement && (
                <Button size="sm" variant="outline" onClick={() => setDialogue('decaissement')}>
                  Constater le décaissement
                </Button>
              )}
              {a.enregistrerRemboursement && (
                <Button size="sm" variant="outline" onClick={() => setDialogue('remboursement')}>
                  Enregistrer un remboursement
                </Button>
              )}
              {a.proposerReamenagement && (
                <Button size="sm" variant="outline" onClick={() => setDialogue('reamenagement')}>
                  Proposer un réaménagement
                </Button>
              )}
              {a.approuverReamenagement && (
                <Button size="sm" onClick={() => executer(() => actions.approuverReamenagement.mutateAsync(p.id), 'Réaménagement approuvé : nouvel échéancier.')} disabled={actions.approuverReamenagement.isPending}>
                  Approuver le réaménagement
                </Button>
              )}
              {a.declarerExigibilite && (
                <Button size="sm" variant="outline" onClick={() => setDialogue('exigibilite')}>
                  Déclarer le départ de l'agent
                </Button>
              )}
              {a.proposerImputation && (
                <Button size="sm" variant="outline" onClick={() => setDialogue('imputation')}>
                  Imputer sur le décompte final
                </Button>
              )}
              {a.validerImputation && (
                <Button size="sm" onClick={() => executer(() => actions.validerImputation.mutateAsync(p.id), 'Imputation validée.')} disabled={actions.validerImputation.isPending}>
                  Valider l'imputation ({a.validerImputation})
                </Button>
              )}
            </span>
          </CardTitle>
          <CardDescription>
            {p.libelleNature} · {montantDevise(p.montantTotal, dev)} à {p.tauxAnnuel} % l'an sur {p.dureeMois} mois · mensualité {montantDevise(p.mensualite, dev)}
            {p.montantRachat > 0 ? ` · rachat ${montantDevise(p.montantRachat, dev)}, net versé ${montantDevise(p.montantNet, dev)}` : ''}
            {p.origine === 'REPRISE' ? (p.situation ? ` · situation reprise au ${dateFr(p.situation.date)} : capital restant dû ${montantDevise(p.situation.capitalRestantDu, dev)}, ${p.situation.echeancesPayees} échéance(s) réglée(s) avant la mise en service, ${p.situation.echeancesRestantes} restante(s)` : ' · repris du classeur de suivi') : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi label="Capital restant dû" valeur={montantDevise(p.solde.capitalRestant, dev)} detail={`remboursé ${montantDevise(p.solde.capitalRembourse, dev)} de capital`} />
          <Kpi label="Intérêts payés" valeur={montantDevise(p.solde.interetsPayes, dev)} detail={`prévus ${montantDevise(p.solde.interetsPrevus, dev)}`} />
          <Kpi label="Prochaine échéance" valeur={p.solde.prochaineEcheance ? dateFr(p.solde.prochaineEcheance.date) : p.statut === 'SOLDE' ? 'soldé' : '—'} detail={p.solde.prochaineEcheance ? `${montantDevise(p.solde.prochaineEcheance.mensualite, dev)} · n° ${p.solde.prochaineEcheance.numero}` : p.statut === 'A_DECAISSER' ? 'après le décaissement' : ''} />
          <Kpi label="Échéances" valeur={`${p.solde.echeancesConfirmees} / ${p.solde.echeancesTotal}`} detail={p.solde.nonRetenues ? `${p.solde.nonRetenues} non retenue(s)` : 'confirmées'} accent={p.solde.nonRetenues ? 'alerte' : undefined} />
        </CardContent>
      </Card>

      <Card data-testid="decaissement">
        <CardHeader>
          <CardTitle>Décaissement</CardTitle>
          <CardDescription>FED rattaché au prêt, établi par un profil habilité à émettre pour la Direction financière ; le paiement enregistré en trésorerie marque le prêt décaissé (au plus tard le 20 du mois) ; la première échéance tombe à la fin du mois suivant.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <span className="text-muted-foreground">FED</span>
            <div>
              {d.fed ? (
                <Link to={`/fed/${d.fed.id}`} className="text-primary hover:underline">
                  {d.fed.numero ?? 'FED (brouillon)'} · {d.fed.statut.toLowerCase()} · {d.fed.etatPaiement === 'PAYE' ? 'payé' : d.fed.etatPaiement === 'LIQUIDE' ? 'mis en paiement' : 'à payer'}
                </Link>
              ) : (
                '—'
              )}
            </div>
          </div>
          <div>
            <span className="text-muted-foreground">Décaissé le</span>
            <div>{p.dateDecaissement ? dateFr(p.dateDecaissement) : 'en attente'}</div>
          </div>
          <div>
            <span className="text-muted-foreground">Banque / référence</span>
            <div>{[p.banque, p.referencePaiement].filter(Boolean).join(' · ') || '—'}</div>
          </div>
          <div>
            <span className="text-muted-foreground">Première échéance</span>
            <div>{p.premiereEcheance ? dateFr(p.premiereEcheance) : '—'}</div>
          </div>
          {d.engagementsRachetes.length > 0 && (
            <div className="sm:col-span-2 lg:col-span-4">
              <span className="text-muted-foreground">Engagements rachetés</span>
              <ul className="mt-1 space-y-1">
                {d.engagementsRachetes.map((e) => (
                  <li key={e.id} className="flex flex-wrap items-center gap-2">
                    {e.source === 'FONAREV' ? 'FONAREV' : e.banque} · {montantDevise(e.soldeRestantDu, e.devise)}
                    {e.extinction ? <Badge variant="outline">éteint le {dateFr(e.extinction.date)} · {e.extinction.reference}</Badge> : <Badge variant="secondary">en cours jusqu'au courrier de clôture</Badge>}
                    {a.enregistrerExtinction && !e.extinction && (
                      <Button size="sm" variant="ghost" onClick={() => setDialogue({ extinction: e.id })}>
                        Enregistrer le courrier de clôture
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {p.reamenagement && (
        <Card className="border-sky-300" data-testid="reamenagement">
          <CardHeader>
            <CardTitle className="text-base">Réaménagement {p.reamenagement.approuveLe ? 'approuvé' : p.reamenagement.accepteLe ? 'accepté, en attente du Directeur Général' : 'proposé'}</CardTitle>
            <CardDescription>
              Capital restant {montantDevise(p.reamenagement.capitalRestant, dev)} sur {p.reamenagement.dureeMois} mois, mensualité {montantDevise(p.reamenagement.mensualite, dev)} · {p.reamenagement.commentaire}
              {p.reamenagement.approuveLe ? ` · approuvé le ${dateHeureFr(p.reamenagement.approuveLe)}` : ''}
            </CardDescription>
          </CardHeader>
          {a.repondreReamenagement && (
            <CardContent className="flex gap-2">
              <Button onClick={() => executer(() => actions.reponseReamenagement.mutateAsync({ id: p.id, accepte: true }), 'Réaménagement accepté.')}>Accepter</Button>
              <Button variant="outline" onClick={() => executer(() => actions.reponseReamenagement.mutateAsync({ id: p.id, accepte: false }), 'Réaménagement refusé.')}>
                Refuser
              </Button>
            </CardContent>
          )}
        </Card>
      )}

      {p.imputation && (
        <Card className="border-amber-300" data-testid="imputation">
          <CardHeader>
            <CardTitle className="text-base">Imputation sur le décompte final {p.imputation.executeeLe ? 'exécutée' : 'en attente de validation'}</CardTitle>
            <CardDescription>
              {montantDevise(p.imputation.montant, dev)}
              {p.imputation.commentaire ? ` · ${p.imputation.commentaire}` : ''} · validations : DRH {p.imputation.validations.DRH ? '✓' : '…'}, DF {p.imputation.validations.DF ? '✓' : '…'}, DG {p.imputation.validations.DG ? '✓' : '…'}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {d.recouvrement && (
        <Card className="border-destructive/40" data-testid="recouvrement-pret">
          <CardHeader>
            <CardTitle className="text-base">Recouvrement (droit de suite) · {d.recouvrement.statut === 'CLOS' ? 'clos' : 'en cours'}</CardTitle>
            <CardDescription>
              Exigible {montantDevise(d.recouvrement.montantExigible, dev)}, imputé {montantDevise(d.recouvrement.imputeDecompte, dev)}, reliquat {montantDevise(d.recouvrement.reliquat, dev)}, encaissé {montantDevise(d.recouvrement.encaisse, dev)}, restant {montantDevise(d.recouvrement.restant, dev)} ·{' '}
              <Link to="/rh/prets/recouvrement" className="text-primary hover:underline">
                dossier de recouvrement
              </Link>
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <Card data-testid="echeancier-fige">
        <CardHeader>
          <CardTitle>Échéancier {p.versionEcheancier ? `(version ${p.versionEcheancier}${d.versions.length > 1 ? ` sur ${d.versions.length}` : ''})` : ''}</CardTitle>
          <CardDescription>Figé au décaissement ; versionné en cas de remboursement anticipé ou de réaménagement. Une échéance n'est soldée que par une retenue ou un remboursement confirmé.</CardDescription>
        </CardHeader>
        <CardContent>
          {!d.echeancier.length ? (
            <p className="text-sm text-muted-foreground">Échéancier établi au décaissement.</p>
          ) : (
            <div className="max-h-96 overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>N°</TableHead>
                    <TableHead>Échéance</TableHead>
                    <TableHead className="text-right">Capital en début</TableHead>
                    <TableHead className="text-right">Intérêts</TableHead>
                    <TableHead className="text-right">Principal</TableHead>
                    <TableHead className="text-right">Mensualité</TableHead>
                    <TableHead className="text-right">Constaté</TableHead>
                    <TableHead className="text-right">Capital restant</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.echeancier.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell>{e.numero}</TableCell>
                      <TableCell>{dateFr(e.date)}</TableCell>
                      <TableCell className="num text-right">{montantDevise(e.capitalDebut, dev)}</TableCell>
                      <TableCell className="num text-right">{montantDevise(e.interets, dev)}</TableCell>
                      <TableCell className="num text-right">{montantDevise(e.principal, dev)}</TableCell>
                      <TableCell className="num text-right">{montantDevise(e.mensualite, dev)}</TableCell>
                      <TableCell className="num text-right">{montantDevise(e.montantConstate, dev)}</TableCell>
                      <TableCell className="num text-right">{montantDevise(e.capitalFin, dev)}</TableCell>
                      <TableCell>
                        <Badge variant={e.statut === 'CONFIRMEE' ? 'default' : e.statut === 'NON_RETENUE' ? 'destructive' : e.statut === 'ANNULEE' ? 'outline' : 'secondary'}>{e.libelleStatut}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="remboursements">
        <CardHeader>
          <CardTitle>Remboursements constatés</CardTitle>
          <CardDescription>Retenues confirmées par la paie, remboursements anticipés, règlements directs, régularisations, décompte final, recouvrement ; jamais modifiés, contre-passés le cas échéant.</CardDescription>
        </CardHeader>
        <CardContent>
          {!d.remboursements.length ? (
            <p className="text-sm text-muted-foreground">Aucun remboursement constaté.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead className="text-right hidden sm:table-cell">Capital</TableHead>
                  <TableHead className="text-right hidden sm:table-cell">Intérêts</TableHead>
                  <TableHead className="hidden md:table-cell">Échéance</TableHead>
                  <TableHead className="hidden lg:table-cell">Référence · commentaire</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {d.remboursements.map((r) => (
                  <TableRow key={r.id} className={r.montant < 0 ? 'text-muted-foreground' : undefined}>
                    <TableCell>{dateFr(r.date)}</TableCell>
                    <TableCell>
                      {r.libelleSource}
                      {r.contrepassationDeId && <Badge variant="outline" className="ml-1">contre-passation</Badge>}
                      {r.contrepasse && <Badge variant="outline" className="ml-1">contre-passé</Badge>}
                    </TableCell>
                    <TableCell className="num text-right">{montantDevise(r.montant, dev)}</TableCell>
                    <TableCell className="num text-right hidden sm:table-cell">{montantDevise(r.capital, dev)}</TableCell>
                    <TableCell className="num text-right hidden sm:table-cell">{montantDevise(r.interets, dev)}</TableCell>
                    <TableCell className="hidden md:table-cell">{r.echeanceNumero ? `n° ${r.echeanceNumero}` : 'capital'}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs">
                      {[r.reference, r.commentaire].filter(Boolean).join(' · ')} <span className="text-muted-foreground">({r.saisiPar})</span>
                    </TableCell>
                    <TableCell>
                      {a.contrepasser && r.montant > 0 && !r.contrepasse && (
                        <Button size="sm" variant="ghost" onClick={() => setDialogue({ contrepasser: r.id })}>
                          Contre-passer
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {d.retenues.length > 0 && (
        <Card data-testid="retenues-pret">
          <CardHeader>
            <CardTitle>Retenues sur paie</CardTitle>
            <CardDescription>États mensuels dans lesquels ce prêt figure.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mois</TableHead>
                  <TableHead>État</TableHead>
                  <TableHead className="text-right">Préparé</TableHead>
                  <TableHead className="text-right">Confirmé</TableHead>
                  <TableHead>Écart</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {d.retenues.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      {mien ? r.mois : (
                        <Link to={`/rh/prets/retenues/${r.mois}`} className="text-primary hover:underline">
                          {r.mois}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>{r.statut.toLowerCase()}</TableCell>
                    <TableCell className="num text-right">{montantDevise(r.montantPrepare, dev)}</TableCell>
                    <TableCell className="num text-right">{r.montantConfirme === null ? '—' : montantDevise(r.montantConfirme, dev)}</TableCell>
                    <TableCell>{r.ecartMotif ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {dialogue === 'fed' && <FedDialog d={d} onClose={() => setDialogue(null)} onConfirmer={(corps) => executer(() => actions.etablirFed.mutateAsync({ id: p.id, corps }), 'FED établi en brouillon, rattaché au prêt.')} chargement={actions.etablirFed.isPending} />}
      {dialogue === 'decaissement' && <DecaissementDialog d={d} onClose={() => setDialogue(null)} onConfirmer={(corps) => executer(() => actions.decaissement.mutateAsync({ id: p.id, corps }), 'Décaissement constaté : échéancier établi.')} chargement={actions.decaissement.isPending} />}
      {dialogue === 'remboursement' && <RemboursementDialog d={d} onClose={() => setDialogue(null)} onConfirmer={(corps) => executer(() => actions.remboursement.mutateAsync({ id: p.id, corps }), 'Remboursement enregistré.')} chargement={actions.remboursement.isPending} />}
      {dialogue === 'reamenagement' && <ReamenagementDialog d={d} onClose={() => setDialogue(null)} onConfirmer={(dureeMois, commentaire) => executer(() => actions.reamenagement.mutateAsync({ id: p.id, dureeMois, commentaire }), 'Réaménagement proposé à l’agent.')} chargement={actions.reamenagement.isPending} />}
      {dialogue === 'exigibilite' && <TexteDateDialog titre="Départ de l'agent" description="Le capital restant dû et les intérêts échus deviennent exigibles ; l'imputation sur le décompte final se propose ensuite (validation DRH, DF, DG), puis le droit de suite sur le reliquat." libelle="Déclarer" avecDate onClose={() => setDialogue(null)} onConfirmer={(texte, date) => executer(() => actions.exigibilite.mutateAsync({ id: p.id, date: date ?? auj(), motif: texte }), 'Prêt exigible.')} chargement={actions.exigibilite.isPending} />}
      {dialogue === 'imputation' && <ImputationDialog d={d} onClose={() => setDialogue(null)} onConfirmer={(montant, commentaire) => executer(() => actions.imputation.mutateAsync({ id: p.id, montant, commentaire }), 'Imputation proposée ; validations du DRH, du DF et du DG attendues.')} chargement={actions.imputation.isPending} />}
      {dialogue && typeof dialogue === 'object' && 'contrepasser' in dialogue && <TexteDateDialog titre="Contre-passer le remboursement" description="Un remboursement constaté ne se modifie jamais : une écriture inverse, motivée et tracée, l'annule." libelle="Contre-passer" minimum={10} onClose={() => setDialogue(null)} onConfirmer={(motif) => executer(() => actions.contrepasser.mutateAsync({ id: p.id, remboursementId: dialogue.contrepasser, motif }), 'Remboursement contre-passé.')} chargement={actions.contrepasser.isPending} />}
      {dialogue && typeof dialogue === 'object' && 'extinction' in dialogue && <TexteDateDialog titre="Courrier de demande de clôture" description="Le courrier sollicitant la clôture du prêt racheté fait foi de son extinction (point 26) : date et référence." libelle="Enregistrer l'extinction" avecDate minimum={2} onClose={() => setDialogue(null)} onConfirmer={(reference, date) => executer(() => actions.extinction.mutateAsync({ engagementId: dialogue.extinction, date: date ?? auj(), reference }), 'Engagement éteint.')} chargement={actions.extinction.isPending} />}
    </div>
  );
}

function FedDialog({ d, onClose, onConfirmer, chargement }: { d: DetailPretAccorde; onClose: () => void; onConfirmer: (corps: { ligneBudgetaireId: string; modePaiement?: string; semaineDecaissement?: string; beneficiaireRachat?: string }) => void; chargement: boolean }) {
  const lignes = useBudgetLignes('DFN');
  const [ligne, setLigne] = useState('');
  const [mode, setMode] = useState('Virement');
  const [semaine, setSemaine] = useState('');
  const [beneficiaireRachat, setBeneficiaireRachat] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Établir le FED du prêt {d.pret.numero}</DialogTitle>
          <DialogDescription>
            FED prérempli pour la Direction financière : {montantDevise(d.pret.montantNet, d.pret.devise)} versés à {d.pret.agent.nom}
            {d.pret.montantRachat > 0 ? ` et ${montantDevise(d.pret.montantRachat, d.pret.devise)} affectés au rachat` : ''} ; il suit ensuite le circuit habituel et le paiement en trésorerie marque le prêt décaissé.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1.5">
            <Label>Ligne budgétaire des prêts au personnel</Label>
            <SelectNatif value={ligne} onChange={(e) => setLigne(e.target.value)}>
              <option value="">Choisir une ligne de la Direction financière</option>
              {(lignes.data ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.code} · {l.libelle}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="fed-mode">Mode de paiement</Label>
              <Input id="fed-mode" value={mode} onChange={(e) => setMode(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fed-semaine">Semaine de décaissement souhaitée</Label>
              <Input id="fed-semaine" type="date" value={semaine} onChange={(e) => setSemaine(e.target.value)} />
            </div>
          </div>
          {d.pret.montantRachat > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="fed-rachat">Bénéficiaire du rachat (banque, compte)</Label>
              <Input id="fed-rachat" value={beneficiaireRachat} onChange={(e) => setBeneficiaireRachat(e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer({ ligneBudgetaireId: ligne, modePaiement: mode || undefined, semaineDecaissement: semaine || undefined, beneficiaireRachat: beneficiaireRachat || undefined })} disabled={!ligne || chargement}>
            Établir le FED
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecaissementDialog({ d, onClose, onConfirmer, chargement }: { d: DetailPretAccorde; onClose: () => void; onConfirmer: (corps: { dateDecaissement: string; banque?: string; reference?: string }) => void; chargement: boolean }) {
  const [date, setDate] = useState(auj());
  const [banque, setBanque] = useState('');
  const [reference, setReference] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Constater le décaissement · {d.pret.numero}</DialogTitle>
          <DialogDescription>Paiement exécuté hors de la voie du module (ou reprise) : date, banque ou caisse, référence. Le FED payé dans le module déclenche sinon le constat automatiquement.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="dec-date">Date de décaissement</Label>
            <Input id="dec-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dec-ref">Référence</Label>
            <Input id="dec-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="dec-banque">Banque ou caisse</Label>
            <Input id="dec-banque" value={banque} onChange={(e) => setBanque(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer({ dateDecaissement: date, banque: banque || undefined, reference: reference || undefined })} disabled={!date || chargement}>
            Constater
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RemboursementDialog({ d, onClose, onConfirmer, chargement }: { d: DetailPretAccorde; onClose: () => void; onConfirmer: (corps: { montant: number; date: string; source: 'ANTICIPE' | 'DIRECT' | 'REGULARISATION'; reference?: string; commentaire?: string }) => void; chargement: boolean }) {
  const [source, setSource] = useState<'ANTICIPE' | 'DIRECT' | 'REGULARISATION'>('ANTICIPE');
  const [montant, setMontant] = useState('');
  const [date, setDate] = useState(auj());
  const [reference, setReference] = useState('');
  const [commentaire, setCommentaire] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remboursement · {d.pret.numero}</DialogTitle>
          <DialogDescription>Anticipé : capital réduit sans pénalité, échéances à venir recalculées (nouvelle version). Direct ou régularisation : imputé sur les échéances ouvertes, intérêts d'abord. Capital restant dû : {montantDevise(d.pret.solde.capitalRestant, d.pret.devise)}.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Nature</Label>
            <SelectNatif value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
              <option value="ANTICIPE">Remboursement anticipé (total ou partiel)</option>
              <option value="DIRECT">Règlement direct</option>
              <option value="REGULARISATION">Régularisation</option>
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rb-montant">Montant ({d.pret.devise})</Label>
            <Input id="rb-montant" type="number" min={0.01} step="0.01" value={montant} onChange={(e) => setMontant(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rb-date">Date</Label>
            <Input id="rb-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rb-ref">Référence</Label>
            <Input id="rb-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="rb-com">Commentaire</Label>
            <Textarea id="rb-com" rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer({ montant: Number(montant), date, source, reference: reference || undefined, commentaire: commentaire || undefined })} disabled={!(Number(montant) > 0) || !date || chargement}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReamenagementDialog({ d, onClose, onConfirmer, chargement }: { d: DetailPretAccorde; onClose: () => void; onConfirmer: (dureeMois: number, commentaire: string) => void; chargement: boolean }) {
  const restantes = d.echeancier.filter((e) => e.statut === 'PREVUE' || e.statut === 'PREPAREE').length;
  const [duree, setDuree] = useState(String(restantes || 12));
  const [commentaire, setCommentaire] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Proposer un réaménagement · {d.pret.numero}</DialogTitle>
          <DialogDescription>Nouvelle durée restante pour le capital restant dû ({montantDevise(d.pret.solde.capitalRestant, d.pret.devise)}) ; l'agent accepte, puis le Directeur Général approuve ; l'échéancier est versionné.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="ra-duree">Durée restante (mois)</Label>
            <Input id="ra-duree" type="number" min={1} value={duree} onChange={(e) => setDuree(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ra-com">Motif (10 caractères au moins)</Label>
            <Textarea id="ra-com" rows={3} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer(Number(duree), commentaire.trim())} disabled={!(Number(duree) >= 1) || commentaire.trim().length < 10 || chargement}>
            Proposer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImputationDialog({ d, onClose, onConfirmer, chargement }: { d: DetailPretAccorde; onClose: () => void; onConfirmer: (montant: number, commentaire?: string) => void; chargement: boolean }) {
  const exigible = Math.round((d.pret.solde.capitalRestant + Math.max(0, d.pret.solde.interetsEchus - d.pret.solde.interetsPayes)) * 100) / 100;
  const [montant, setMontant] = useState(String(exigible));
  const [commentaire, setCommentaire] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Imputation sur le décompte final · {d.pret.numero}</DialogTitle>
          <DialogDescription>Solde exigible : {montantDevise(exigible, d.pret.devise)}. Le montant imputé est validé par le DRH, le DF et le DG ; le reliquat éventuel ouvre un dossier de recouvrement (droit de suite).</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="im-montant">Montant imputé ({d.pret.devise})</Label>
            <Input id="im-montant" type="number" min={0} max={exigible} step="0.01" value={montant} onChange={(e) => setMontant(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="im-com">Commentaire</Label>
            <Textarea id="im-com" rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer(Number(montant), commentaire || undefined)} disabled={!(Number(montant) >= 0) || Number(montant) > exigible || chargement}>
            Proposer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TexteDateDialog({ titre, description, libelle, avecDate, minimum = 3, onClose, onConfirmer, chargement }: { titre: string; description: string; libelle: string; avecDate?: boolean; minimum?: number; onClose: () => void; onConfirmer: (texte: string, date?: string) => void; chargement: boolean }) {
  const [texte, setTexte] = useState('');
  const [date, setDate] = useState(auj());
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titre}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {avecDate && (
          <div className="space-y-1.5">
            <Label htmlFor="td-date">Date</Label>
            <Input id="td-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        )}
        <Textarea rows={3} value={texte} onChange={(e) => setTexte(e.target.value)} placeholder={`${minimum} caractères au moins`} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer(texte.trim(), avecDate ? date : undefined)} disabled={texte.trim().length < minimum || chargement}>
            {libelle}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
